import { clamp, isEqual, isNil, size } from 'lodash-es';
import React, { useId, useState } from 'react';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { NoSsr } from '@mui/material';

import { NEW_FEATURE_DATA_ATTRIBUTE, NEW_FEATURE_REVISION_COOKIE } from 'app/constants';
import useIsDesktop from 'hooks/use-is-desktop';
import useIsMounted from 'hooks/use-is-mounted';
import { useUserInfo } from 'queries/user-action/use-user-info';

import Backdrop from './components/Backdrop';
import FeatureItem from './components/FeatureItem';
import SvgMask from './components/SvgMask';
import { ITEMS, REVISION } from './constants';
import useCookie from './hooks/use-cookie';
import useQuerySelector from './hooks/use-query-selector';

const NewFeatureImplementation: React.FC = () => {
    const { data: user } = useUserInfo();

    const isDesktop = useIsDesktop();
    const isMounted = useIsMounted();

    const [index, setIndex] = useState(0);
    const [revision, setRevision] = useCookie(NEW_FEATURE_REVISION_COOKIE);

    const maskId = useId();
    const enabled =
        isMounted &&
        isDesktop &&
        size(ITEMS) > 0 &&
        !isEqual(revision, REVISION) &&
        !isNil(user) &&
        size(user.lastLogin) > 1;

    const element = useQuerySelector({
        selector: `[${NEW_FEATURE_DATA_ATTRIBUTE}=${ITEMS.at(index)?.selector}]`,
        attribute: NEW_FEATURE_DATA_ATTRIBUTE,
        debounce: 250,
        enabled
    });

    const active = enabled && !!element;

    return (
        <Backdrop open={active} delay={1_000} mask={`url(#${maskId})`}>
            <SvgMask id={maskId} element={element} offset={4} />
            <FeatureItem
                key={index}
                element={element}
                progress={`${index + 1} / ${size(ITEMS)}`}
                title={ITEMS.at(index)?.title}
                description={ITEMS.at(index)?.description}
                backButton={
                    index === 0
                        ? {
                              label: 'Skip',
                              onClick: () => setRevision(REVISION)
                          }
                        : {
                              label: 'Back',
                              onClick: () => setIndex((index) => clamp(index - 1, 0, size(ITEMS) - 1)),
                              icon: <ChevronLeft color="inherit" />
                          }
                }
                nextButton={
                    index === size(ITEMS) - 1
                        ? {
                              label: 'Got it',
                              onClick: () => setRevision(REVISION)
                          }
                        : {
                              label: 'Next',
                              onClick: () => setIndex((index) => clamp(index + 1, 0, size(ITEMS) - 1)),
                              icon: <ChevronRight color="inherit" />
                          }
                }
            />
        </Backdrop>
    );
};

const NewFeature: React.FC = () => (
    <NoSsr defer>
        <NewFeatureImplementation />
    </NoSsr>
);

export default NewFeature;
